/* =========================================================
   Nani — Sizing & Fit Guide
   - Charts tab switching
   - Finder category + unit toggles, live size lookup
   ========================================================= */
(function () {
  'use strict';

  // ---------- Chart row data (in inches) ----------
  // Mirrors data structure used in production. Used by the finder
  // for size lookup; the printed charts read DOM, so keep both in sync
  // via section schema (chart rows are baked into the section markup).
  const CHARTS = {
    swim: {
      // cols: Size, Dress, Underbust, Waist
      rows: [
        ['XXS', '00',    '22 – 24', '22 – 23'],
        ['XS',  '0–2',   '25 – 27', '24 – 25'],
        ['S',   '4–6',   '28 – 30', '26 – 27'],
        ['M',   '6–8',   '31 – 33', '28 – 31'],
        ['L',   '10–12', '34 – 36', '32 – 34'],
        ['XL',  '14–16', '37 – 39', '35 – 38'],
        ['XXL', '18–20', '40 – 42', '39 – 41'],
        ['2X',  '22W',   '42 – 44', '42 – 44'],
        ['3X',  '24W',   '46 – 48', '46 – 48'],
      ],
      keys: { underbust: 2, waist: 3 },
    },
    apparel: {
      // cols: Size, Numerical, Waist, Hip
      rows: [
        ['XS',  '0–2',   '25 – 27', '36 – 38'],
        ['S',   '4–6',   '28 – 30', '38 – 40'],
        ['M',   '8–10',  '31 – 32', '40 – 42'],
        ['L',   '12–14', '32 – 33', '42 – 43'],
        ['XL',  '16',    '34 – 36', '44 – 45'],
        ['XXL', '18',    '37 – 40', '46 – 47'],
      ],
      keys: { waist: 2, hip: 3 },
    },
    kids: {
      // cols: Age, Hip, Chest
      rows: [
        ['2T', '18',      '18.5'],
        ['3T', '19',      '19'],
        ['4T', '20',      '19.5'],
        ['5T', '21',      '20'],
        ['6',  '22 – 23', '21 – 22'],
        ['8',  '23 – 24', '22 – 23'],
        ['10', '24 – 25', '23 – 24'],
        ['12', '25 – 26', '24 – 25'],
        ['14', '26 – 27', '25 – 26'],
      ],
      keys: { hip: 1, chest: 2 },
    },
    mens: {
      // cols: Size, Waist, Chest
      rows: [
        ['S',   '26 – 28', '34 – 36'],
        ['M',   '30 – 32', '38 – 40'],
        ['L',   '34 – 36', '42 – 44'],
        ['XL',  '38 – 40', '46 – 48'],
        ['XXL', '42 – 44', '50 – 52'],
      ],
      keys: { waist: 1, chest: 2 },
    },
  };

  function inRange(val, range) {
    const m = String(range).replace(/–/g, '-').match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
    if (!m) {
      const single = parseFloat(range);
      return !Number.isNaN(single) && Math.abs(val - single) < 0.75;
    }
    return val >= +m[1] && val <= +m[2];
  }

  function toIn(v, unit) { return unit === 'cm' ? v / 2.54 : v; }

  function findSize(category, measurements, unit) {
    const def = CHARTS[category];
    if (!def) return null;
    const keys = Object.keys(def.keys);
    const matches = [];
    const provided = [];
    keys.forEach((k) => {
      const raw = parseFloat(measurements[k]);
      if (!raw || raw <= 0) return;
      const val = toIn(raw, unit);
      const colIdx = def.keys[k];
      const row = def.rows.find((r) => inRange(val, r[colIdx]));
      provided.push(k);
      if (row) matches.push({ key: k, size: row[0] });
    });
    if (!matches.length) return null;

    const labelize = (k) => k.charAt(0).toUpperCase() + k.slice(1);
    if (matches.length === 1) {
      return {
        size: matches[0].size,
        mixed: false,
        note: `Based on your ${matches[0].key} alone. Add another measurement for a sharper match.`,
      };
    }

    // pick the larger of the two sizes (Nani always rounds up)
    const sizesOrder = def.rows.map((r) => r[0]);
    const sorted = matches.slice().sort((a, b) => sizesOrder.indexOf(a.size) - sizesOrder.indexOf(b.size));
    const smaller = sorted[0].size;
    const larger = sorted[sorted.length - 1].size;
    if (smaller === larger) {
      const both = matches.map((x) => x.key).join(' and ');
      return { size: larger, mixed: false, note: `Your ${both} both land at ${larger}.` };
    }
    return {
      size: larger,
      mixed: true,
      smaller,
      larger,
      note: `${labelize(sorted[0].key)} reads ${smaller}, ${sorted[sorted.length - 1].key} reads ${larger}. We recommend the larger size — Nani fabric stretches with you.`,
    };
  }

  // ---------- Chart tab switcher ----------
  function initCharts(root) {
    const tabs = root.querySelectorAll('[data-sf-charts-tab]');
    const panels = root.querySelectorAll('[data-sf-charts-panel]');
    if (!tabs.length) return;
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const id = tab.getAttribute('data-sf-charts-tab');
        tabs.forEach((t) => {
          const active = t.getAttribute('data-sf-charts-tab') === id;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        panels.forEach((p) => {
          p.classList.toggle('is-active', p.getAttribute('data-sf-charts-panel') === id);
        });
      });
    });
  }

  // ---------- Finder ----------
  function initFinder(root) {
    const state = { tab: 'swim', unit: 'in', m: {} };

    const tabBtns = root.querySelectorAll('[data-sf-finder-tab]');
    const panels = root.querySelectorAll('[data-sf-finder-panel]');
    const unitBtns = root.querySelectorAll('[data-sf-unit]');
    const unitLabels = root.querySelectorAll('[data-sf-unit-label]');
    const resultEl = root.querySelector('[data-sf-result]');
    const resetBtns = root.querySelectorAll('[data-sf-reset]');

    function activePanel() {
      return root.querySelector('[data-sf-finder-panel="' + state.tab + '"]');
    }

    function readInputs() {
      state.m = {};
      const panel = activePanel();
      if (!panel) return;
      panel.querySelectorAll('input[data-sf-field]').forEach((inp) => {
        const key = inp.getAttribute('data-sf-field');
        if (inp.value) state.m[key] = inp.value;
      });
    }

    function render() {
      const r = findSize(state.tab, state.m, state.unit);
      if (!r) {
        resultEl.classList.remove('is-found');
        resultEl.classList.add('is-empty');
        return;
      }
      resultEl.classList.remove('is-empty');
      resultEl.classList.add('is-found');
      resultEl.querySelector('[data-sf-result-size]').textContent = r.size;
      const split = resultEl.querySelector('[data-sf-result-split]');
      if (r.mixed) {
        split.hidden = false;
        split.querySelector('[data-sf-result-smaller]').textContent = r.smaller;
        split.querySelector('[data-sf-result-larger]').textContent = r.larger;
      } else {
        split.hidden = true;
      }
      resultEl.querySelector('[data-sf-result-note]').textContent = r.note;
    }

    function setTab(id) {
      state.tab = id;
      tabBtns.forEach((b) => {
        const a = b.getAttribute('data-sf-finder-tab') === id;
        b.classList.toggle('is-active', a);
        b.setAttribute('aria-selected', a ? 'true' : 'false');
      });
      panels.forEach((p) => {
        p.classList.toggle('is-active', p.getAttribute('data-sf-finder-panel') === id);
      });
      // Reset measurements + inputs of new panel
      const panel = activePanel();
      if (panel) panel.querySelectorAll('input[data-sf-field]').forEach((i) => (i.value = ''));
      state.m = {};
      render();
    }

    function setUnit(u) {
      state.unit = u;
      unitBtns.forEach((b) => {
        const a = b.getAttribute('data-sf-unit') === u;
        b.classList.toggle('is-active', a);
        b.setAttribute('aria-checked', a ? 'true' : 'false');
      });
      unitLabels.forEach((l) => (l.textContent = u));
      readInputs();
      render();
    }

    tabBtns.forEach((b) => b.addEventListener('click', () => setTab(b.getAttribute('data-sf-finder-tab'))));
    unitBtns.forEach((b) => b.addEventListener('click', () => setUnit(b.getAttribute('data-sf-unit'))));
    root.querySelectorAll('input[data-sf-field]').forEach((inp) => {
      inp.addEventListener('input', () => {
        readInputs();
        render();
      });
    });
    resetBtns.forEach((b) =>
      b.addEventListener('click', () => {
        const panel = activePanel();
        if (panel) panel.querySelectorAll('input[data-sf-field]').forEach((i) => (i.value = ''));
        state.m = {};
        render();
      })
    );

    render();
  }

  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-sf-charts]').forEach((el) => {
      if (el.dataset.sfInited === '1') return;
      el.dataset.sfInited = '1';
      initCharts(el);
    });
    root.querySelectorAll('[data-sf-finder]').forEach((el) => {
      if (el.dataset.sfInited === '1') return;
      el.dataset.sfInited = '1';
      initFinder(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: re-init when a section is re-rendered (script tag
  // with same src won't re-execute, but the new DOM needs listeners).
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
